import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay';

// components
import PdpRespSimilarItemCarousel from './PdpRespSimilarItemCarousel/PdpRespSimilarItemCarousel';
import PdpRespSimilarItemsContainer from './PdpRespSimilarItemsContainer/PdpRespSimilarItemsContainer';
import { UnpurchasableHeader, viewSimilarItems, ItemsSimilarToHeader } from '../pdpMessages';

// actions
import { hasFiredLandingItemsImpressions } from '../../actions/pdpRespSimilarItemsActions';

// helpers
import get from 'lodash.get';
import scrollIntoView from 'scroll-into-view';
import {
    trackLandingItemClick,
    trackLandingViewAllClick,
    trackLandingItemImpressions,
    trackLandingItemsDisplayed,
} from '../tracking/similarItemsTracking';

// styles
import styles from './PdpRespSimilarItemsLanding.scss';

// constants
const PURCHASABLE_ITEM_COUNT = 6;

export class PdpRespSimilarItemsLanding extends React.Component {
    constructor(props) {
        super(props);

        this.onClickItem = this.onClickItem.bind(this);
        this.onClickViewMore = this.onClickViewMore.bind(this);
    }

    componentDidMount() {
        trackLandingItemsDisplayed();
    }

    componentDidUpdate() {
        const landingItems = this.getLandingItems();
        if (landingItems.length && !this.props.similarItemsState.hasFiredImpressions) {
            trackLandingItemImpressions(landingItems, this.isPdpTypeDisplayUnpurchasable());
            this.props.updateHasFiredImpressions();
        }
    }

    scrollToPdpRespSimilarItemsGrid() {
        const gridEl = get(this.props, 'similarItemsState.gridEl');
        if (gridEl) {
            scrollIntoView(gridEl, {
                time: 500,
            });
        }
    }

    onClickItem(index) {
        trackLandingItemClick(
            index,
            this.getLandingItems()[index],
            this.isPdpTypeDisplayUnpurchasable()
        );
    }

    onClickViewMore() {
        this.scrollToPdpRespSimilarItemsGrid();

        // fire tracking
        trackLandingViewAllClick();
    }

    isPdpTypeDisplayUnpurchasable() {
        const { item } = this.props;
        const isListingNotListed = item.isListingNotListed && !get(item, 'seller.isInternal');
        return (item.isListingUnlisted || item.isSold) && !isListingNotListed;
    }

    getLandingItems() {
        const similarItems = get(this.props, 'similarItems.edges') || [];

        // Unpurchasable Pdp
        if (this.isPdpTypeDisplayUnpurchasable()) {
            return similarItems;
        }

        // GROWTH-1429: Test Similar Items on Pdp
        if (this.props.relayVariables.showSimilarItems) {
            return similarItems.slice(0, PURCHASABLE_ITEM_COUNT);
        }

        return [];
    }

    render() {
        const itemTitle = <i>{this.props.item.title}</i>;
        let landingItems = this.getLandingItems();
        let additionalLandingItems;

        let header;
        let subtitle;
        if (this.isPdpTypeDisplayUnpurchasable()) {
            // Unpurchasable Pdp
            header = <UnpurchasableHeader title={itemTitle} />;
            subtitle = viewSimilarItems;
        } else if (this.props.relayVariables.showSimilarItems) {
            // GROWTH-1429: Test Similar Items on Pdp
            subtitle = <ItemsSimilarToHeader title={itemTitle} />;
        }

        if (!landingItems.length) {
            return null;
        } else if (landingItems.length > PURCHASABLE_ITEM_COUNT) {
            additionalLandingItems = landingItems.slice(PURCHASABLE_ITEM_COUNT);
            landingItems = landingItems.slice(0, PURCHASABLE_ITEM_COUNT);
        }

        const itemProps = {
            onClick: this.onClickItem,
            isClient: this.props.relayVariables.isClient,
        };

        return (
            <PdpRespSimilarItemsContainer
                isDark
                header={header}
                subtitle={subtitle}
                onClickViewMore={this.onClickViewMore}
                dataTn="pdp-similar-landing-grid"
            >
                {/* Grid of Items */}
                <div className={`rowFlex ${styles.landingItems}`}>
                    {landingItems.map(({ node }, index) => (
                        <div
                            key={node.serviceId}
                            className={`colLg2 colMd4 colXs6 ${styles.landingItem}`}
                        >
                            <PdpRespSimilarItemCarousel item={node} index={index} {...itemProps} />
                        </div>
                    ))}
                </div>
                {additionalLandingItems ? (
                    <div className={`rowFlex ${styles.landingItems}`}>
                        {additionalLandingItems.map(({ node }, index) => (
                            <div
                                key={node.serviceId}
                                className={`colLg2 colMd4 colXs6 ${styles.landingItem}`}
                            >
                                <PdpRespSimilarItemCarousel
                                    item={node}
                                    index={index + 6}
                                    {...itemProps}
                                />
                            </div>
                        ))}
                    </div>
                ) : null}
            </PdpRespSimilarItemsContainer>
        );
    }
}

PdpRespSimilarItemsLanding.propTypes = {
    relayVariables: PropTypes.shape({
        showSimilarItems: PropTypes.bool.isRequired,
        isClient: PropTypes.bool.isRequired,
    }).isRequired,
    item: PropTypes.object.isRequired,
    similarItems: PropTypes.object,

    // redux
    similarItemsState: PropTypes.shape({
        gridEl: PropTypes.object,
        hasFiredImpressions: PropTypes.bool.isRequired,
    }).isRequired,
    updateHasFiredImpressions: PropTypes.func.isRequired,
};

// Redux Map Functions
function mapStateToProps({ similarItems }) {
    return {
        similarItemsState: {
            gridEl: similarItems.gridEl,
            hasFiredImpressions: similarItems.ecommerceImpressions.hasFiredLandingItems,
        },
    };
}
function mapDispatchToProps(dispatch) {
    return {
        updateHasFiredImpressions: () => dispatch(hasFiredLandingItemsImpressions()),
    };
}

export default createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(PdpRespSimilarItemsLanding),
    {
        item: graphql`
            fragment PdpRespSimilarItemsLanding_item on Item {
                title
                isSold
                isListingNotListed
                isListingUnlisted
                seller {
                    isInternal
                }
            }
        `,
        similarItems: graphql`
            fragment PdpRespSimilarItemsLanding_similarItems on moreLikeThisConnection {
                edges {
                    node {
                        serviceId
                        categoryPath
                        ecommerceTrackingParams(userId: $userId) @include(if: $isClient)
                        ...PdpRespSimilarItemCarousel_item
                    }
                }
            }
        `,
    }
);
